/**
 * Daily Bridge Summary — admin ops digest of the last 24h of MT5 activity.
 * Backed by GET /api/mt5/admin/bridge-summary. Also emitted daily at 23:00
 * PHT via web push + in-app notification. "Run now" previews on demand.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = {
  bg: "#0d1117", card: "#111722", border: "#1f2733", text: "#e6e7ec",
  muted: "#7f8792", gold: "#C9A227", green: "#22c55e", red: "#ef4444", blue: "#3b82f6",
};

const Stat = ({ label, value, color }) => (
  <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", minWidth: 96 }}>
    <div style={{ color: C.muted, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 1 }}>{label}</div>
    <div style={{ color: color || C.text, fontSize: 22, fontWeight: 800, fontFamily: "monospace", marginTop: 4 }}>{value}</div>
  </div>
);

export default function Mt5BridgeSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/mt5/admin/bridge-summary`, { params: { limit: 14 }, withCredentials: true });
      setData(data); setErr("");
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to load bridge summary"); }
    finally { setLoading(false); }
  }, []);

  const runNow = async () => {
    setRunning(true);
    try { await axios.post(`${API}/mt5/admin/bridge-summary/run`, {}, { withCredentials: true }); await load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed to run digest"); }
    finally { setRunning(false); }
  };

  useEffect(() => { load(); }, [load]);

  const latest = data?.summaries?.[0];
  const history = (data?.summaries || []).slice(1);

  return (
    <div style={{ padding: "8px 24px 8px" }} data-testid="mt5-bridge-summary">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h2 style={{ color: C.text, fontSize: 18, margin: 0 }}>Daily Bridge Summary</h2>
          <div style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>
            Auto-sends to admins daily at 23:00 PHT (push + notification). Last 24h.
          </div>
        </div>
        <button data-testid="bridge-summary-run-btn" onClick={runNow} disabled={running}
          style={{ background: C.gold, border: "none", color: "#07090f", borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 700, cursor: running ? "wait" : "pointer", opacity: running ? 0.6 : 1 }}>
          {running ? "Running…" : "↻ Run now"}
        </button>
      </div>

      {err && <div style={{ color: C.red, marginBottom: 10 }} data-testid="bridge-summary-error">{err}</div>}
      {loading ? (
        <div style={{ color: C.muted }} data-testid="bridge-summary-loading">Loading…</div>
      ) : !latest ? (
        <div style={{ color: C.muted }} data-testid="bridge-summary-empty">No summary yet — click “Run now” to generate one.</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }} data-testid="bridge-summary-stats">
            <Stat label="Fills" value={latest.fills} color={C.green} />
            <Stat label="Rejects" value={latest.rejects} color={latest.rejects ? C.red : C.text} />
            <Stat label="Expired" value={latest.expired} color={C.muted} />
            <Stat label="Blocked" value={latest.blocks} color={latest.blocks ? C.gold : C.text} />
            <Stat label="Off-session" value={latest.off_session_blocks} color={latest.off_session_blocks ? C.gold : C.text} />
            <Stat label="Fill rate" value={latest.fill_rate == null ? "—" : `${latest.fill_rate}%`} color={C.blue} />
            <Stat label="EAs online" value={`${latest.eas_online}/${latest.eas_total}`} color={latest.eas_online ? C.green : C.muted} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginBottom: 16 }}>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14 }}>
              <div style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Top rejection / block reasons</div>
              {(latest.top_reasons || []).length === 0 ? (
                <div style={{ color: C.muted, fontSize: 13 }}>None in the last 24h 🎉</div>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 16, color: C.text, fontSize: 13, lineHeight: 1.9 }} data-testid="bridge-summary-reasons">
                  {latest.top_reasons.map((r) => (
                    <li key={r.reason}><span style={{ color: C.red }}>×{r.count}</span> {r.reason}</li>
                  ))}
                </ul>
              )}
            </div>
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14 }}>
              <div style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Fill-rate leaders</div>
              <div style={{ fontSize: 13, color: C.text, lineHeight: 1.9 }}>
                <div><span style={{ color: C.green }}>▲ Best:</span> {latest.best_fill_rate ? `${latest.best_fill_rate.email} — ${latest.best_fill_rate.fill_rate}% (${latest.best_fill_rate.trades} trades)` : "—"}</div>
                <div><span style={{ color: C.red }}>▼ Worst:</span> {latest.worst_fill_rate ? `${latest.worst_fill_rate.email} — ${latest.worst_fill_rate.fill_rate}% (${latest.worst_fill_rate.trades} trades)` : "—"}</div>
              </div>
            </div>
          </div>

          {history.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>History</div>
              {history.map((h) => (
                <div key={h.date} style={{ color: C.muted, fontSize: 12, fontFamily: "monospace", padding: "3px 0", borderBottom: `1px solid ${C.border}` }} data-testid="bridge-summary-history-row">
                  <span style={{ color: C.text }}>{h.date}</span> · {h.summary}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
